import { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';

const FILTERS = [
  { id: '', label: 'All' },
  { id: 'awaiting_attestation', label: 'Awaiting attested copy' },
  { id: 'pending', label: 'Pending' },
  { id: 'under_review', label: 'Under Review' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
];

function errorText(error) {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' ? detail : 'Request failed';
}

export default function ScholarshipAdminPage() {
  const { token } = useContext(AuthContext);
  const headers = { Authorization: `Bearer ${token}` };
  const [filter, setFilter] = useState('');
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = async (status = filter) => {
    const response = await axios.get(`${API}/scholarship/admin/applications`, {
      headers,
      params: status ? { status } : {},
    });
    setRows(response.data || []);
  };

  useEffect(() => {
    load('').catch((err) => setError(errorText(err)));
  }, []);

  const open = async (row) => {
    setError('');
    setMessage('');
    const response = await axios.get(`${API}/scholarship/admin/applications/${row.id}`, { headers });
    setSelected(response.data);
  };

  const download = async (path, filename) => {
    const response = await axios.get(`${API}${path}`, { headers, responseType: 'blob' });
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const updateStatus = async (status) => {
    if (!selected) return;
    setError('');
    setMessage('');
    const data = new FormData();
    data.append('status', status);
    data.append('note', note);
    try {
      await axios.post(`${API}/scholarship/admin/applications/${selected.application.id}/status`, data, { headers });
      setMessage(`Status updated to ${status.replace('_', ' ')}. The applicant was notified.`);
      await open(selected.application);
      await load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const sendReview = async () => {
    try {
      const response = await axios.post(
        `${API}/scholarship/admin/applications/${selected.application.id}/send-to-testimonials`,
        {},
        { headers }
      );
      setMessage(response.data.already_sent
        ? 'This review is already in the testimonials workflow.'
        : 'Review sent to the testimonials workflow for approval.');
      await open(selected.application);
    } catch (err) {
      setError(errorText(err));
    }
  };

  const app = selected?.application;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold text-gray-900">Scholarship applications</h1>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => download('/scholarship/admin/export.csv', 'scholarship-applications.csv')}>Excel</Button>
            <Button variant="outline" onClick={() => download('/scholarship/admin/export.pdf', 'scholarship-applications.pdf')}>PDF</Button>
            <Button variant="outline" onClick={() => download('/scholarship/admin/audit.log', 'scholarship-audit.log')}>Audit log</Button>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {FILTERS.map((item) => (
            <Button
              key={item.id || 'all'}
              variant={filter === item.id ? 'default' : 'outline'}
              className={filter === item.id ? 'bg-[#A89968] hover:bg-[#8f8356] text-white' : ''}
              onClick={() => {
                setFilter(item.id);
                load(item.id).catch((err) => setError(errorText(err)));
              }}
            >
              {item.label}
            </Button>
          ))}
        </div>
        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
        {message && <p className="mt-4 text-sm text-green-700">{message}</p>}
        <div className="mt-4 overflow-x-auto rounded-xl bg-white shadow-sm border border-gray-100">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                {['Number', 'Name', 'Status', 'Rental', 'Review', 'Paused'].map((heading) => (
                  <th key={heading} className="px-3 py-2 font-medium">{heading}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t cursor-pointer hover:bg-gray-50" onClick={() => open(row)}>
                  <td className="px-3 py-2">{row.application_number}</td>
                  <td className="px-3 py-2">{row.full_name}</td>
                  <td className="px-3 py-2">{row.status_label}</td>
                  <td className="px-3 py-2">{row.rental_completed ? 'Completed' : 'None yet'}</td>
                  <td className="px-3 py-2">{row.review_submitted ? 'Yes' : 'No'}</td>
                  <td className="px-3 py-2">{row.disbursement_paused ? 'Paused' : '—'}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td className="px-3 py-6 text-gray-500" colSpan={6}>No applications in this filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {app && (
          <section className="mt-6 rounded-xl bg-white p-5 shadow-sm border border-gray-100 space-y-3">
            <h2 className="text-xl font-semibold">{app.application_number} · {app.full_name}</h2>
            <p className="text-sm text-gray-700">{app.email} · {app.phone} · {app.gender} · DOB {app.dob}</p>
            <p className="text-sm text-gray-700">{app.address}</p>
            <p className="text-sm text-gray-700">{app.school_name} · {app.class_year} · {app.course} · ID {app.student_id} · {app.marks}</p>
            <p className="text-sm text-gray-700">Guardian: {app.guardian_name} ({app.guardian_occupation}) · Income ₹{app.family_income} · Family {app.family_members}</p>
            <p className="text-sm text-gray-700">Clubs: {app.clubs}</p>
            <p className="text-sm text-gray-700">Service: {app.community_service}</p>
            <p className="text-sm text-gray-700">Awards: {app.awards}</p>
            <p className="text-sm text-gray-700">Why it matters: {app.importance}</p>
            <p className="text-sm text-gray-700">
              Signature: {app.signature_name} · Date: {app.declaration_date}. Rentals: {app.rental_count}. Review: {app.review_submitted ? 'submitted' : 'missing'}.
            </p>
            {app.review_text && <p className="text-sm whitespace-pre-wrap bg-gray-50 rounded-md p-3">{app.review_text}</p>}
            <div className="flex flex-wrap gap-2">
              {app.photo && (
                <Button variant="outline" onClick={() => download(`/scholarship/admin/files/${app.id}/${app.photo.id}`, app.photo.filename || 'photo.jpg')}>
                  Student photo
                </Button>
              )}
              {(app.attested_copies || []).map((file) => (
                <Button key={file.id} variant="outline" onClick={() => download(`/scholarship/admin/files/${app.id}/${file.id}`, file.filename)}>
                  Attested: {file.filename}
                </Button>
              ))}
              {(app.documents || []).map((file) => (
                <Button key={file.id} variant="outline" onClick={() => download(`/scholarship/admin/files/${app.id}/${file.id}`, file.filename)}>
                  {file.filename}
                </Button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <input className="rounded-md border px-2 py-1 text-sm" placeholder="Note for the status log" value={note} onChange={(event) => setNote(event.target.value)} />
              {app.status === 'submitted' && (
                <Button variant="outline" onClick={() => updateStatus('under_review')}>Mark under review</Button>
              )}
              {app.status === 'under_review' && (
                <Button className="text-white hover:opacity-90" style={{ backgroundColor: 'var(--primary)' }} onClick={() => updateStatus('approved')}>Approve</Button>
              )}
              {(app.status === 'submitted' || app.status === 'under_review') && (
                <Button variant="outline" onClick={() => updateStatus('rejected')}>Reject</Button>
              )}
              <Button variant="outline" onClick={sendReview}>Send review to testimonials</Button>
            </div>
            {selected.utilizations?.length > 0 && (
              <div>
                <h3 className="font-medium">Utilization</h3>
                <ul className="text-sm text-gray-700">
                  {selected.utilizations.map((item) => (
                    <li key={item.id}>{item.period}: ₹{item.amount} — {item.description}</li>
                  ))}
                </ul>
              </div>
            )}
            <div>
              <h3 className="font-medium">Status log</h3>
              <ul className="text-sm text-gray-700">
                {(selected.status_updates || []).map((item) => (
                  <li key={item.id}>{item.created_at} · {item.to_status}{item.note ? ` · ${item.note}` : ''}</li>
                ))}
              </ul>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
